<script setup lang="ts">
import { addYears, differenceInYears } from 'date-fns'
import { z } from 'zod'

const props = defineProps<{
  context: {
    value: string
    node: { input: (v: Date | null) => void }
    transformDate?: ((date: Date) => Date) | undefined
    inputType?: 'date' | 'datetime' | 'month' | 'year'
    size: 'small' | 'medium' | 'large'
    disabled?: boolean
    showRelativeYears?: boolean
  } & Record<string, any>
}>()

const handleInput = (value: number | null) => {
  if (value === null) {
    props.context.node.input(null)
    return
  }

  const valueToUpdate = z.coerce.date().safeParse(value).success ? new Date(value) : null
  const transformer = props.context.transformDate
  if (transformer && valueToUpdate) {
    const transformed = transformer(valueToUpdate)
    props.context.node.input(transformed)
  } else {
    props.context.node.input(valueToUpdate)
  }
}

const value = computed(() => {
  if (props.context.value) {
    return new Date(props.context.value).getTime()
  }
  return null
})

const valueInYears = computed(() => {
  if (!value.value) {
    return null
  }
  return Math.abs(differenceInYears(new Date(value.value), new Date()))
})

const relativeValue = ref<number>(valueInYears.value ?? 0)

// Set relativeValue when value changes
watch(valueInYears, (years) => {
  if (relativeValue.value === years) {
    return
  }
  relativeValue.value = years ?? 0
})

// Set value when relativeValue changes
watch(relativeValue, (years) => {
  if (years === valueInYears.value || !years) {
    return
  }
  handleInput(addYears(new Date(), -Math.abs(years)).getTime())
})
</script>

<template>
  <n-input-group>
    <n-date-picker
      :value="value"
      :size="context.size ?? 'large'"
      :type="context.inputType ?? 'date'"
      clearable
      :disabled="context.disabled"
      class="w-full"
      @update:value="e => handleInput(e)"
    />

    <n-input-number
      v-if="context.showRelativeYears"
      v-model:value="relativeValue"
      :size="context.size ?? 'large'"
      class="w-[180px]"
    >
      <template #suffix>
        {{ $t('heatProtection.reception.birthDate.suffix') }}
      </template>
    </n-input-number>
  </n-input-group>
</template>
